import { useQuery } from '@tanstack/react-query'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { useTranslate } from 'src/localization/Trns'
import type { AccountCollectionFlowCompletionResponse } from 'src/queries/payments/payments.types'
import {
  useCompleteVendorAccountCollectionFlow,
  useConfirmVendorAccountCollectionFlow,
  useCreateVendorAccountCollectionFlow,
  useRetireVendorPaymentMethod,
  vendorQueries,
} from 'src/queries/vendors/vendors.queries'
import {
  ConfirmVendorAccountCollectionFlowInput,
  Vendor,
  VendorAccountCollectionFlowResponse,
  VendorPaymentMethodDetailsResponse,
  VendorPaymentMethodResponse,
  VendorPendingAccountDetailsResponse,
} from 'src/queries/vendors/vendors.types'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  BackendErrorPayload,
  getBackendErrorPayload,
  NETWORK_ERROR_PAYLOAD,
} from 'src/utils/error.utils'
import { isBillPayAtomicAccountSwapEnabled } from 'src/utils/featureFlags'

import useVendorAccountConfirmation, {
  UseVendorAccountConfirmationResult,
} from './useVendorAccountConfirmation'
import {
  acknowledgementCopyText,
  VENDOR_ACKNOWLEDGEMENT,
} from './vendorAcknowledgement'

/** Message key for a failed retire, resolved to copy at render time. */
type RemoveErrorKind = 'not-found' | 'concurrent' | 'unexpected'

/**
 * The re-add half's render phase (State B), driven by the reused confirmation
 * hook and whether Modern Treasury has accepted the hosted form. Mirrors the
 * add-vendor flow's own phase machine — the SAME lifecycle code
 * (`useVendorAccountConfirmation`, the account-collection-flow mutation hooks),
 * not a second copy of it.
 */
export type VendorReAddPhase =
  | 'collecting'
  | 'acknowledgement'
  | 'confirming'
  | 'confirmation-pending'
  | 'confirmation-error'

type SubmittedFlow = Pick<VendorAccountCollectionFlowResponse, 'flowId'>

export interface UseVendorPaymentMethodResult {
  /**
   * Does the vendor have a current ACH method? Computed once here from the
   * vendor DETAIL, falling back to a just-confirmed method only until that
   * detail re-read lands (see `confirmedMethod` below). Never from whether the
   * bank read succeeded — that read returns 200 for a retired method too, and
   * its fields are null on a provider outage.
   *
   * SPIKE: this is no longer the SINGLE branch predicate. It used to be, because
   * a vendor could only ever be collecting an account while it had none. With
   * the atomic swap it can be collecting a REPLACEMENT while its current account
   * is still live and still payable, so the tab branches on
   * `hasCurrentAccount && !isReplacing`. See `isReplacing`.
   */
  hasCurrentAccount: boolean

  // ── State A: read the current account ──────────────────────────────────────
  details: VendorPaymentMethodDetailsResponse | undefined
  isDetailsLoading: boolean
  /** A provider read failure (502). State A stays State A — em-dashes + an error
   * affordance, never the embed. */
  isDetailsError: boolean

  // ── State A: remove behind a confirm ───────────────────────────────────────
  isConfirmingRemoval: boolean
  openRemoveConfirm: () => void
  cancelRemoveConfirm: () => void
  confirmRemoval: () => void
  isRemoving: boolean
  removeError: string | null

  // ── SPIKE: replace the current account without ever retiring it ────────────
  /**
   * The user is collecting a replacement while the current account is still
   * live. The vendor stays PAYABLE for the whole of this — the swap happens in
   * one transaction at confirm time, and abandoning changes nothing.
   */
  isReplacing: boolean
  /**
   * Whether the atomic-swap affordances may be offered at all
   * (`bill_pay_atomic_account_swap`). False hides "Replace account" from the
   * kebab entirely rather than showing an item that no-ops.
   */
  canReplace: boolean
  /**
   * The disclosure panel shown BEFORE the replace flow starts, not after.
   *
   * This is the honesty fix. Removal tells the user up front that payments
   * awaiting approval will be cancelled, and they consent before acting. A swap
   * cancels those same payments, so without this the user would only find out
   * after filling in a bank form — or not at all. The consequence has to be
   * disclosed at the point of consent, so the confirm gates entry into the flow
   * rather than gating the final Confirm.
   */
  isConfirmingReplace: boolean
  openReplaceConfirm: () => void
  cancelReplaceConfirm: () => void
  /** Accept the disclosure and open the collection embed. */
  confirmReplace: () => void
  /**
   * Back out of a replacement that is still COLLECTING. Only offered at that
   * stage, and only true there: an abandoned flow is still `PENDING`, so a later
   * Replace hits `FlowClaim.Relaunch` and reuses it transparently.
   *
   * Past the collect stage the flow row is at `AWAITING_ACKNOWLEDGEMENT`, a
   * Modern Treasury external account exists and a VENDOR_BANK_ACCOUNT_ADDED
   * activity row has been written -- so backing out there is NOT free, and the
   * server has no cancel to undo it. See ReplaceInProgressNote.
   */
  cancelReplace: () => void

  // ── State B: re-add through the Modern Treasury embed ──────────────────────
  phase: VendorReAddPhase
  /** Remount key for the embed — bumped to retry a create-flow that 409'd on
   * the one-open-flow rule. */
  embedKey: number
  /** A just-retired vendor can still carry a stale open collection flow, so
   * creating a new one 409s. Surface a retry rather than a dead end. */
  hasOpenFlowConflict: boolean
  retryReAdd: () => void
  createFlowForEmbed: () => Promise<VendorAccountCollectionFlowResponse>
  completeFlowForEmbed: (
    flowId: string,
  ) => Promise<AccountCollectionFlowCompletionResponse>
  handleCollectionLinked: () => void
  /** The details the embed just collected, shown on the acknowledgement step so
   * the user confirms against what will be saved (EX-40844). */
  pendingDetails: VendorPendingAccountDetailsResponse | undefined
  isPendingDetailsLoading: boolean
  /** A provider read failure (502) on the DISPLAY. */
  isPendingDetailsError: boolean
  /**
   * Whether there are details on screen to acknowledge. Gates the checkbox and
   * Confirm (EX-40844): the whole point of this step is that the user attests
   * to something they can SEE, so a still-loading or failed read must not be
   * confirmable. This matters most on the resume path, where the person
   * confirming may never have typed these details and has no other way to know
   * them.
   */
  canAcknowledgeDetails: boolean

  // ── Shared footer contribution (owned by the shell, not this tab) ──────────
  /** True while the ACH acknowledgement is pending — the shell footer shows the
   * ACH checkbox + Confirm and Add. */
  showFooter: boolean
  acknowledged: boolean
  setAcknowledged: (value: boolean) => void
  handleConfirmAndAdd: () => void
  confirmation: UseVendorAccountConfirmationResult
  confirmationErrorMessage: string | null
}

function statusOf(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined
  if ('status' in error && typeof error.status === 'number') return error.status
  return undefined
}

/**
 * SPIKE. The open flow's id from an awaiting-acknowledgement 409, or undefined for every other
 * error.
 *
 * This exists because the flow id is otherwise unrecoverable. It is minted by create and held only
 * in this hook's React state, so anything that unmounts the tab — closing the flyout, switching
 * vendor, a reload — loses it, and no read on the API hands it back. Before the API carried it
 * here, that left the vendor permanently unable to finish OR replace: every subsequent create got
 * the same 409, and the conflict panel's only affordance re-created and got it again.
 */
function awaitingAcknowledgementFlowId(error: unknown): string | undefined {
  if (statusOf(error) !== 409) return undefined
  const payload = backendPayloadOf(error)
  if (payload?.errorCode !== 'VENDOR_ACCOUNT_AWAITING_ACKNOWLEDGEMENT') {
    return undefined
  }
  const flowId = (payload as { flowId?: unknown }).flowId
  return typeof flowId === 'string' && flowId.trim() !== '' ? flowId : undefined
}

/**
 * Unwraps whichever error shape reached us. Axios errors carry the body on `response.data`, while
 * a normalized QueryError has no `.response` at all — just `error`/`status`, with the axios error
 * tucked on a non-enumerable `originalError` (see src/queries/AGENTS.md). Reading only the axios
 * shape silently yields the network fallback for every QueryError, so the code check would never
 * fire and the resume would quietly never happen. Same three steps as useVendorDetailsForm.
 */
function backendPayloadOf(
  error: unknown,
): BackendErrorPayload<string> | undefined {
  const raw =
    (error as { originalError?: unknown } | null)?.originalError ?? error
  const fromResponse = getBackendErrorPayload(raw)
  if (fromResponse !== NETWORK_ERROR_PAYLOAD) return fromResponse
  if (error && typeof error === 'object' && 'error' in error) {
    return error as BackendErrorPayload<string>
  }
  return undefined
}

/**
 * Owns the Payment Information tab's data + orchestration for one vendor: the
 * live bank-details read, the retire-behind-a-confirm, and the re-add through
 * the existing Modern Treasury collection → acknowledgement → confirm lifecycle.
 * Reuses the account-collection-flow mutation hooks and
 * {@link useVendorAccountConfirmation} verbatim — the tab adds no second copy of
 * that sequence. Lives at the shell level (VendorEditLoaded) so the single
 * shell-owned footer can render the ACH acknowledgement, and unmounts with the
 * keyed edit view so its state resets per vendor.
 */
export default function useVendorPaymentMethod({
  organizationId,
  vendor,
}: {
  organizationId: string
  vendor: Vendor
}): UseVendorPaymentMethodResult {
  const translate = useTranslate()
  const dispatch = useDispatch()
  // Gated here rather than at the kebab, so every replace-only affordance is
  // switched by one read. Off, `isReplacing` can never become true, which
  // collapses the tab back to the two states EX-40772 shipped.
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  const swapEnabled = isBillPayAtomicAccountSwapEnabled(
    remoteConfig,
    organizationId,
  )

  // A just-confirmed method, held until the vendor detail re-read catches up.
  //
  // Without it there is a window right after a successful confirm where the
  // predicate is still false: clearing `submittedFlow` drops `phase` to
  // `collecting` in the same synchronous React batch, while the detail refetch
  // is still in flight. State B re-renders, MtAccountCollection remounts, and
  // it creates a flow once on mount — but the vendor now HAS a current ACH
  // method, so the API answers `PaymentMethodAlreadyExists` (409), which this
  // hook then mislabels as a stale open flow. Transient in the happy path,
  // terminal if the refetch fails, since the retry only 409s again.
  //
  // Writing the confirmation into the query cache does NOT close this: React
  // Query notifies observers on a microtask, so the embed still mounts for one
  // render. A plain state fallback flips the predicate in the very same batch.
  //
  // It has to be RETIRED once the detail catches up, not just left to `??`.
  // `??` only defers to the server when the server says something non-null, so
  // a later authoritative `paymentMethod: null` — another actor retires the
  // account and a refetch observes it — would fall through to this stale value
  // and pin the card open for the life of the flyout. The effect below drops it
  // the moment the detail reports the same id, so the fallback covers the
  // post-confirm gap and nothing beyond it.
  const [confirmedMethod, setConfirmedMethod] =
    useState<VendorPaymentMethodResponse | null>(null)

  // Retire the fallback as soon as the detail reports the confirmed method:
  // from then on `vendor.paymentMethod` is the only source, null included.
  useEffect(() => {
    if (confirmedMethod && vendor.paymentMethod?.id === confirmedMethod.id) {
      setConfirmedMethod(null)
    }
  }, [confirmedMethod, vendor.paymentMethod?.id])

  // The one branch predicate. `paymentMethod` is `null` when absent in the
  // mapped domain vendor, so State A ⇔ a non-null method on the DETAIL.
  const currentMethod = vendor.paymentMethod ?? confirmedMethod
  const hasCurrentAccount = currentMethod != null
  const paymentMethodId = currentMethod?.id

  // State A read. Its `enabled` guard needs all three ids, so it self-disables
  // in State B (no method id) — the read never runs when there is no account.
  const detailsQuery = useQuery(
    vendorQueries.paymentMethodDetails(
      organizationId,
      vendor.id,
      paymentMethodId,
    ),
  )

  // ── Remove ──────────────────────────────────────────────────────────────
  const retireMutation = useRetireVendorPaymentMethod(organizationId)
  const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false)
  const [removeErrorKind, setRemoveErrorKind] =
    useState<RemoveErrorKind | null>(null)
  // A synchronous guard: the mutation's `isPending` is a render-time snapshot,
  // so two clicks in the same tick both read `false` and would fire twice. The
  // ref flips immediately, so the second click is a no-op even before a
  // re-render. (The endpoint is idempotent anyway — this just keeps the UI from
  // firing redundant calls.)
  const retiringRef = useRef(false)

  const openRemoveConfirm = useCallback((): void => {
    setRemoveErrorKind(null)
    // SPIKE: mirrors openReplaceConfirm clearing the removal panel. Without it
    // both consent panels render stacked, each in its own role="alert", one
    // offering Remove and one Continue -- reachable because the replace
    // disclosure leaves the tab in State A with the kebab still available.
    setIsConfirmingReplace(false)
    setIsConfirmingRemoval(true)
  }, [])

  const cancelRemoveConfirm = useCallback((): void => {
    setIsConfirmingRemoval(false)
    setRemoveErrorKind(null)
  }, [])

  const confirmRemoval = useCallback((): void => {
    if (!paymentMethodId || retiringRef.current) return
    retiringRef.current = true
    setRemoveErrorKind(null)
    retireMutation.mutate(
      { vendorId: vendor.id, paymentMethodId },
      {
        // On success the vendor re-reads with no method → State B, so the
        // confirm panel unmounts with the card; closing it here also covers the
        // brief window before the refetch lands.
        onSuccess: () => {
          retiringRef.current = false
          setIsConfirmingRemoval(false)
          // Drop the held method too: the account is gone, so leaving it set
          // would pin the tab to State A after a successful retire.
          setConfirmedMethod(null)
        },
        onError: (error) => {
          retiringRef.current = false
          switch (statusOf(error)) {
            case 404:
              setRemoveErrorKind('not-found')
              break
            case 409:
              setRemoveErrorKind('concurrent')
              break
            default:
              setRemoveErrorKind('unexpected')
          }
        },
      },
    )
  }, [paymentMethodId, retireMutation, vendor.id])

  // ── SPIKE: replace ────────────────────────────────────────────────────────
  const [isReplacing, setIsReplacing] = useState(false)
  const [isConfirmingReplace, setIsConfirmingReplace] = useState(false)

  const openReplaceConfirm = useCallback((): void => {
    if (!swapEnabled) return
    setRemoveErrorKind(null)
    setIsConfirmingRemoval(false)
    setIsConfirmingReplace(true)
  }, [swapEnabled])

  const cancelReplaceConfirm = useCallback((): void => {
    setIsConfirmingReplace(false)
  }, [])

  const confirmReplace = useCallback((): void => {
    if (!swapEnabled) return
    setIsConfirmingReplace(false)
    setIsReplacing(true)
  }, [swapEnabled])

  const removeError =
    removeErrorKind === 'not-found'
      ? translate(
          'This bank account could not be found. It may have already been removed. Close this and reopen the vendor.',
        )
      : removeErrorKind === 'concurrent'
        ? translate(
            'This vendor was updated at the same time. Please try removing the account again.',
          )
        : removeErrorKind === 'unexpected'
          ? translate(
              'Something went wrong removing this account. Please try again.',
            )
          : null

  // ── Re-add (reused Modern Treasury lifecycle) ─────────────────────────────
  const createFlowMutation = useCreateVendorAccountCollectionFlow()
  const completeFlowMutation =
    useCompleteVendorAccountCollectionFlow(organizationId)
  const confirmFlowMutation =
    useConfirmVendorAccountCollectionFlow(organizationId)

  const [submittedFlow, setSubmittedFlow] = useState<SubmittedFlow | null>(null)
  // Phase B read (EX-40844): the details the embed just collected, so the
  // acknowledgement shows what is about to be saved. No `enabled` override —
  // the factory's own arg-presence guard is already exactly the condition, and
  // `onConfirmed` clears `submittedFlow`, so it self-disables after confirm
  // rather than refetching into a 409.
  const pendingDetailsQuery = useQuery(
    vendorQueries.pendingAccountDetails(
      organizationId,
      vendor.id,
      submittedFlow?.flowId,
    ),
  )
  const [acknowledged, setAcknowledged] = useState(false)
  const [hasOpenFlowConflict, setHasOpenFlowConflict] = useState(false)
  const [embedKey, setEmbedKey] = useState(0)
  const activeFlowRef = useRef<VendorAccountCollectionFlowResponse | null>(null)
  const completionStatusRef = useRef<
    'PENDING' | 'AWAITING_ACKNOWLEDGEMENT' | 'CONFIRMED' | null
  >(null)

  const confirmation = useVendorAccountConfirmation({
    confirmFlow: confirmFlowMutation.mutateAsync,
    onConfirmed: (response) => {
      setConfirmedMethod(response.paymentMethod)
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <span>
              <strong>{translate('Payment details updated')}</strong>{' '}
              {translate(
                '${vendorName} is active with new ACH payment details on file.',
                { vendorName: vendor.name },
              )}
            </span>
          ),
        }),
      )
      // The confirm invalidation re-reads the vendor → PAYABLE → State A card.
      // Clear the local re-add state so a later retire starts clean.
      setSubmittedFlow(null)
      setAcknowledged(false)
      // SPIKE: the swap has landed, so the card comes back showing the NEW
      // account. Leaving this set would keep the tab in the embed.
      setIsReplacing(false)
    },
  })

  const createFlowForEmbed =
    useCallback(async (): Promise<VendorAccountCollectionFlowResponse> => {
      try {
        const flow = await createFlowMutation.mutateAsync(vendor.id)
        activeFlowRef.current = flow
        setHasOpenFlowConflict(false)
        return flow
      } catch (error) {
        // SPIKE: an already-submitted flow is resumed rather than treated as a conflict. The
        // hosted form is done and its details are already on the server, so the honest next step
        // is the acknowledgement — not an error, and emphatically not "create another one", which
        // is what produced the dead end this branch used to fall into. Setting `submittedFlow`
        // moves `phase` to 'acknowledgement', which unmounts the embed and lets
        // `pendingDetailsQuery` fetch what the user already entered.
        const resumableFlowId = awaitingAcknowledgementFlowId(error)
        if (resumableFlowId !== undefined) {
          setAcknowledged(false)
          confirmation.reset()
          setHasOpenFlowConflict(false)
          setSubmittedFlow({ flowId: resumableFlowId })
          throw error
        }
        // One-open-flow: a just-retired vendor may still hold a stale open flow,
        // so the API rejects a second with 409. Surface a retry instead of the
        // embed's own dead-end error (we render the conflict panel in its place).
        if (statusOf(error) === 409) setHasOpenFlowConflict(true)
        throw error
      }
    }, [confirmation, createFlowMutation, vendor.id])

  const completeFlowForEmbed = useCallback(
    async (
      flowId: string,
    ): Promise<AccountCollectionFlowCompletionResponse> => {
      const result = await completeFlowMutation.mutateAsync({
        vendorId: vendor.id,
        flowId,
      })
      completionStatusRef.current = result.status
      return {
        status: result.status === 'PENDING' ? 'PENDING' : 'COMPLETED',
      }
    },
    [completeFlowMutation, vendor.id],
  )

  const handleCollectionLinked = useCallback((): void => {
    // A completion that already resolved CONFIRMED needs no acknowledgement; the
    // complete invalidation re-reads the vendor into State A on its own.
    if (completionStatusRef.current === 'CONFIRMED') return
    const flow = activeFlowRef.current
    if (!flow) return
    setAcknowledged(false)
    confirmation.reset()
    setSubmittedFlow({ flowId: flow.flowId })
  }, [confirmation])

  const retryReAdd = useCallback((): void => {
    setHasOpenFlowConflict(false)
    setEmbedKey((value) => value + 1)
  }, [])

  // SPIKE. Only reachable from the COLLECT stage, where backing out really is
  // free: the current account was never retired, the vendor never stopped being
  // payable, and the flow row is still PENDING -- so a later Replace hits
  // FlowClaim.Relaunch and reuses it rather than 409ing. That is the entire
  // point of the swap; under retire-then-add this button could not exist,
  // because by this point the account would already be gone.
  //
  // Deliberately NOT offered once the hosted form has been submitted: the flow is at
  // AWAITING_ACKNOWLEDGEMENT and there is no server-side cancel, so "keep current account" would
  // be promising something this cannot deliver.
  //
  // Dismissing the flyout at that point is no longer a dead end, though, which is the part that
  // used to be broken regardless of this button: the next create answers
  // VENDOR_ACCOUNT_AWAITING_ACKNOWLEDGEMENT carrying the flow id, and createFlowForEmbed resumes
  // straight to the acknowledgement rather than re-creating. See ReplaceInProgressNote.
  const cancelReplace = useCallback((): void => {
    setIsReplacing(false)
    setIsConfirmingReplace(false)
    setSubmittedFlow(null)
    setAcknowledged(false)
    setHasOpenFlowConflict(false)
    activeFlowRef.current = null
    completionStatusRef.current = null
    confirmation.reset()
    setEmbedKey((value) => value + 1)
  }, [confirmation])

  const phase: VendorReAddPhase =
    confirmation.phase === 'error'
      ? 'confirmation-error'
      : confirmation.phase === 'confirming'
        ? 'confirming'
        : confirmation.phase === 'pending'
          ? 'confirmation-pending'
          : submittedFlow
            ? 'acknowledgement'
            : 'collecting'

  const handleConfirmAndAdd = useCallback((): void => {
    const flowId = submittedFlow?.flowId
    if (
      !flowId ||
      !acknowledged ||
      phase !== 'acknowledgement' ||
      confirmation.isInFlight
    ) {
      return
    }
    const input: ConfirmVendorAccountCollectionFlowInput = {
      vendorId: vendor.id,
      flowId,
      acknowledged: true,
      acknowledgementCopyVersion: VENDOR_ACKNOWLEDGEMENT.version,
      acknowledgementCopyText: acknowledgementCopyText(
        translate,
        VENDOR_ACKNOWLEDGEMENT,
      ),
    }
    confirmation.startConfirmation(input)
  }, [submittedFlow, acknowledged, phase, confirmation, vendor.id, translate])

  const confirmationErrorMessage =
    confirmation.errorKind === 'stale-or-terminal'
      ? translate(
          'This bank account setup can no longer be confirmed. Close this window and start again.',
        )
      : // SPIKE: transient by construction, and the only 409 here that is. Telling this user to
        // start again would be false — the setup is intact and the next attempt very likely wins.
        confirmation.errorKind === 'concurrent-update'
        ? translate(
            'Something else was updating this vendor at the same time. Please try again.',
          )
        : confirmation.errorKind === 'not-found'
          ? translate(
              'This vendor or bank account setup could not be found. Please try again or close this window and start again.',
            )
          : confirmation.errorKind === 'acknowledgement-required'
            ? translate(
                'We could not record your acknowledgement. Please try again.',
              )
            : confirmation.errorKind === 'provider'
              ? translate(
                  'We could not confirm this bank account right now. Please try again.',
                )
              : confirmation.errorKind === 'unexpected'
                ? translate(
                    'We received an unexpected confirmation response. Please try again.',
                  )
                : null

  // SPIKE: `|| isReplacing`. The footer carries the acknowledgement + confirm,
  // and under a swap that sequence runs while the vendor still HAS a current
  // account. Gating it on `!hasCurrentAccount` alone would leave the user on the
  // acknowledgement step with no way to submit it.
  const showFooter =
    (!hasCurrentAccount || isReplacing) &&
    !hasOpenFlowConflict &&
    phase !== 'collecting'

  return {
    hasCurrentAccount,
    details: detailsQuery.data,
    isDetailsLoading: detailsQuery.isLoading,
    isDetailsError: detailsQuery.isError,
    pendingDetails: pendingDetailsQuery.data,
    isPendingDetailsLoading: pendingDetailsQuery.isLoading,
    isPendingDetailsError: pendingDetailsQuery.isError,
    canAcknowledgeDetails: pendingDetailsQuery.data != null,
    isConfirmingRemoval,
    openRemoveConfirm,
    cancelRemoveConfirm,
    confirmRemoval,
    isRemoving: retireMutation.isPending,
    removeError,
    isReplacing,
    canReplace: swapEnabled,
    isConfirmingReplace,
    openReplaceConfirm,
    cancelReplaceConfirm,
    confirmReplace,
    cancelReplace,
    phase,
    embedKey,
    hasOpenFlowConflict,
    retryReAdd,
    createFlowForEmbed,
    completeFlowForEmbed,
    handleCollectionLinked,
    showFooter,
    acknowledged,
    setAcknowledged,
    handleConfirmAndAdd,
    confirmation,
    confirmationErrorMessage,
  }
}
